import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SportsIcon from '@mui/icons-material/Sports';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import type { VenueTypeConfig } from '@lov/shared';
import { useVenueTypes } from '../hooks/useVenueTypes';

interface CategoryPillsProps {
  value: string | 'ALL';
  onChange: (value: string | 'ALL') => void;
}

interface CategoryOption {
  value: string | 'ALL';
  label: string;
  icon: React.ReactNode;
}

const DEFAULT_ICONS: Record<string, React.ReactNode> = {
  GAMING: <SportsEsportsIcon sx={{ fontSize: 18 }} />,
  SNOOKER: <SportsIcon sx={{ fontSize: 18 }} />,
  KIDS_PLAYAREA: <ChildCareIcon sx={{ fontSize: 18 }} />,
  ALL: <GridViewIcon sx={{ fontSize: 18 }} />,
};

const PillsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  overflowX: 'auto',
  paddingBottom: '8px',
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.divider} transparent`,

  // Webkit scrollbar styling
  '&::-webkit-scrollbar': {
    height: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.text.disabled,
  },

  // Desktop: allow wrapping
  [theme.breakpoints.up('md')]: {
    flexWrap: 'wrap',
  },
}));

interface PillButtonProps {
  active: boolean;
}

const PillButton = styled('button')<PillButtonProps>(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 20px',
  borderRadius: '9999px',
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  border: active ? 'none' : `1px solid ${theme.palette.divider}`,
  backgroundColor: active ? theme.palette.success.main : theme.palette.background.paper,
  color: active ? theme.palette.success.contrastText : theme.palette.text.secondary,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  whiteSpace: 'nowrap',
  outline: 'none',

  '&:hover': {
    backgroundColor: active
      ? theme.palette.success.dark
      : 'rgba(0, 0, 0, 0.04)',
    transform: 'translateY(-1px)',
    boxShadow: active
      ? '0 4px 8px rgba(34, 197, 94, 0.2)'
      : '0 2px 4px rgba(0, 0, 0, 0.08)',
  },

  '&:active': {
    transform: 'translateY(0)',
  },

  '&:focus-visible': {
    outline: `2px solid ${active ? theme.palette.success.main : theme.palette.primary.main}`,
    outlineOffset: '2px',
  },

  // Icon color
  '& .MuiSvgIcon-root': {
    color: 'inherit',
  },
}));

export function CategoryPills({ value, onChange }: CategoryPillsProps) {
  const { data: venueTypes } = useVenueTypes();

  const categories: CategoryOption[] = [
    {
      value: 'ALL',
      label: 'All Venues',
      icon: DEFAULT_ICONS.ALL,
    },
    ...venueTypes
      .filter((vt: VenueTypeConfig) => vt.isActive)
      .sort((a: VenueTypeConfig, b: VenueTypeConfig) => a.displayOrder - b.displayOrder)
      .map((vt: VenueTypeConfig) => ({
        value: vt.code,
        label: vt.name,
        icon: DEFAULT_ICONS[vt.code] || <GridViewIcon sx={{ fontSize: 18 }} />,
      })),
  ];

  return (
    <PillsContainer>
      {categories.map((category) => (
        <PillButton
          key={category.value}
          active={value === category.value}
          onClick={() => onChange(category.value)}
          aria-pressed={value === category.value}
          aria-label={`Filter by ${category.label}`}
        >
          {category.icon}
          <span>{category.label}</span>
        </PillButton>
      ))}
    </PillsContainer>
  );
}
